import { ReactNode } from 'react'
import { NavbarButtonId, NavbarButtonCallbacks } from './navbarConfig'
import History from './components/History'
import RemoveHistory from './components/RemoveHistory'
import ExportImage from './ExportImage'
import Zoom from '../Zoom'
import LogoIconBtnWithPopover from '../FloatingInspectorButton/Buttons/LogoIconBtnWithPopover'
import LogoIconAndTextBtn from '../FloatingInspectorButton/Buttons/LogoIconAndTextBtn'
import TryPremiumButton from './components/TryPremiumButton'
import TrySuperButton from './components/TrySuperButton'
import SignInButton from './components/SignInButton'
import UserMenuPopover from './components/UserMenuPopover'
import GuestMenuPopover from './components/GuestMenuPopover'
import NavbarBackButton from './components/NavbarBackButton'
import DownloadAll from './DownloadAll'
import { FloatingInspectorButtonSet } from '@/constants/app-options'

export interface NavbarButtonContext {
  isLoggedIn: boolean
  isOpenPixelManipulationObject: boolean
  disableNavbar: boolean
  disableDownloadAll: boolean
  /**
   * Disabled state for the Settings (user/guest) menu button. Defaults to
   * `disableNavbar`, but the AI Model and Photoshoot product navbars also
   * disable it while their generation is loading.
   */
  disableUserMenu: boolean
  /**
   * When true, the Settings menu hides the `Try Remove objects` row. Used by
   * the AI Model and Photoshoot product navbars; the editor keeps it visible.
   */
  hideTryRemoveObjects: boolean
  premiumSource?: string
  /** Drives the back-button label (e.g. 'Photoshoot' vs 'AI models'). */
  mode?: FloatingInspectorButtonSet
  onBack?: () => void
  callbacks?: NavbarButtonCallbacks
  downloadAllDestination: string
  downloadAllSource: string
}

export function renderNavbarButton(
  id: NavbarButtonId,
  ctx: NavbarButtonContext
): ReactNode {
  switch (id) {
    case 'logoIconAndText':
      return <LogoIconAndTextBtn key={id} disabled={ctx.disableNavbar} />
    case 'logoIconWithPopover':
      return <LogoIconBtnWithPopover key={id} disabled={ctx.disableNavbar} />
    case 'history':
      return ctx.isOpenPixelManipulationObject
        ? <RemoveHistory key={id} />
        : <History key={id} />
    case 'backButton':
      return <NavbarBackButton key={id} disabled={ctx.disableNavbar} onClick={ctx.onBack} mode={ctx.mode} />
    case 'zoom':
      return <Zoom key={id} />
    case 'tryPremium':
      return <TryPremiumButton key={id} disabled={ctx.disableNavbar} premiumSource={ctx.premiumSource} />
    case 'tryPremiumOrange':
      return <TryPremiumButton key={id} disabled={ctx.disableNavbar} premiumSource={ctx.premiumSource} orange hideCrown />
    case 'trySuper':
      return <TrySuperButton key={id} disabled={ctx.disableNavbar} />
    case 'signIn':
      return ctx.isLoggedIn ? null : <SignInButton key={id} disabled={ctx.disableNavbar} />
    case 'exportImage':
      return <ExportImage key={id} disabled={ctx.disableNavbar} />
    case 'downloadAll':
      return <DownloadAll key={id} downloadScope={ctx.callbacks?.downloadAll} destination={ctx.downloadAllDestination} source={ctx.downloadAllSource} disabled={ctx.disableDownloadAll} />
    case 'userMenu':
      return ctx.isLoggedIn
        ? <UserMenuPopover key={id} disabled={ctx.disableUserMenu} hideTryRemoveObjects={ctx.hideTryRemoveObjects} onSignOut={ctx.callbacks?.onSignOut} />
        : <GuestMenuPopover key={id} disabled={ctx.disableUserMenu} hideTryRemoveObjects={ctx.hideTryRemoveObjects} />
    default:
      return null
  }
}
